import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Button, Collapse, UncontrolledTooltip } from 'reactstrap';
import { PickListStatusBadge } from './PickListStatusBadge';
import { PickListPendingSplitBadge } from './PickListPendingSplitBadge';
import { PickListPartialBadge } from './PickListPartialBadge';
import { StatusBadge, type BadgeTone } from '../StatusBadge';
import { SortTh, type SortDir } from '../ui/sortable';
import { formatBookingDate, type PickListSummary } from '../../api/pickLists';
import { PickListHeatBadge } from './PickListHeatBadge';

// SP-6b — Book head badges. Tone fisso, label via t().
const TIPO_TONES: Record<string, BadgeTone> = { L: 'info', W: 'primary', M: 'neutral' };
const CATEGORIA_TONES: Record<string, BadgeTone> = { V: 'neutral', S: 'info' };
// SP-8h — override colore specifico (fuori palette tone): Manuali=arancione, Varia=fucsia.
const TIPO_CLASS: Record<string, string> = { M: 'vp-tag-manuali' };
const CATEGORIA_CLASS: Record<string, string> = { V: 'vp-tag-varia' };

export interface PickListsTableProps {
  rows: PickListSummary[];
  /** SP-5l accordion: the single open pick-list (level 1), or null. */
  openId: number | null;
  onToggle: (id: number) => void;
  renderExpanded: (id: number) => ReactNode;
  // SP-6c — server-side sort + offset pagination.
  sortCol: string | null;
  sortDir: SortDir;
  onSort: (col: string) => void;
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  /** SP-8c — se valorizzato, mostra il tasto "scarica CSV dettaglio" su ogni
   *  riga (solo per il capo magazzino). Assente → nessun tasto. */
  onDownloadCsv?: (id: number) => void;
}

const COLS = 15;

// Fasce di calore ICM (range fissi) — usate nel tooltip esplicativo dell'header.
const HEAT_BANDS = [
  { level: 'cold', range: '0-20' },
  { level: 'fresh', range: '21-40' },
  { level: 'normal', range: '41-60' },
  { level: 'warm', range: '61-80' },
  { level: 'hot', range: '81-100' },
] as const;

export function PickListsTable({
  rows, openId, onToggle, renderExpanded,
  sortCol, sortDir, onSort, page, perPage, total, onPageChange, onDownloadCsv,
}: PickListsTableProps) {
  const { t } = useTranslation('picklist');
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div>
      <div className="table-responsive pl-sticky-wrap">
      <Table className="table-hover align-middle mb-0">
        <thead>
          <tr>
            <SortTh label={t('columns.external_id')} sortKey="external_id" col={sortCol} dir={sortDir} onToggle={onSort} />
            {/* Ragione Sociale in 2a posizione, subito dopo il numero della
                lista (spostata qui il 30/09: in fondo, dopo Tot. qta prel.,
                per leggerla bisognava scorrere quindici colonne). */}
            <SortTh label={t('columns.customer_name')} sortKey="customer_name" col={sortCol} dir={sortDir} onToggle={onSort} />
            <SortTh label={t('columns.list_date')} sortKey="booking_date" col={sortCol} dir={sortDir} onToggle={onSort} />
            <SortTh label={t('columns.type')} sortKey="list_type" col={sortCol} dir={sortDir} onToggle={onSort} />
            <SortTh label={t('columns.category')} sortKey="list_category" col={sortCol} dir={sortDir} onToggle={onSort} />
            <SortTh label={t('columns.status')} sortKey="status" col={sortCol} dir={sortDir} onToggle={onSort} />
            <SortTh label={t('columns.distinct_ean')} title={t('columns.distinct_ean_tooltip')} sortKey="distinct_ean" col={sortCol} dir={sortDir} onToggle={onSort} />
            <SortTh label={t('columns.operator')} sortKey="operator" col={sortCol} dir={sortDir} onToggle={onSort} />
            <SortTh label={t('columns.tasks')} title={t('columns.tasks_tooltip')} sortKey="distinct_locations" col={sortCol} dir={sortDir} onToggle={onSort} />
            <SortTh label={t('columns.locations_picked')} title={t('columns.locations_picked_tooltip')} sortKey="distinct_locations_picked" col={sortCol} dir={sortDir} onToggle={onSort} />
            <SortTh label={t('columns.qty_total')} title={t('columns.qty_total_tooltip')} sortKey="qty_total" col={sortCol} dir={sortDir} onToggle={onSort} />
            <SortTh label={t('columns.qty_picked_total')} title={t('columns.qty_picked_total_tooltip')} sortKey="qty_picked_total" col={sortCol} dir={sortDir} onToggle={onSort} />
            <th>
              <span id="icm-col-info" className="d-inline-flex align-items-center gap-1" style={{ cursor: 'help' }}>
                {t('heat.column')}
                <i className="bi bi-info-circle text-muted" aria-hidden="true" />
              </span>
              <UncontrolledTooltip target="icm-col-info" placement="bottom" autohide={false} innerClassName="vp-icm-tip">
                <div className="text-start">
                  <div className="fw-bold mb-1">{t('heat.info.title')}</div>
                  <div className="text-white-50 mb-2 vp-icm-tip__small">{t('heat.info.range')}</div>
                  <div className="fw-semibold mb-1 vp-icm-tip__small">{t('heat.info.formula_heading')}</div>
                  <ul className="ps-3 mb-2 vp-icm-tip__small">
                    <li>{t('heat.info.irg')}</li>
                    <li>{t('heat.info.ibr')}</li>
                    <li>{t('heat.info.saturation')}</li>
                  </ul>
                  <div className="fw-semibold mb-1 vp-icm-tip__small">{t('heat.info.bands_heading')}</div>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {HEAT_BANDS.map((b) => (
                      <span key={b.level} className={`badge vp-heat vp-heat-${b.level}`}>
                        {b.range} {t(`heat.levels.${b.level}`)}
                      </span>
                    ))}
                  </div>
                  <div className="text-warning d-flex align-items-start gap-1 vp-icm-tip__small">
                    <i className="bi bi-exclamation-triangle-fill mt-1" aria-hidden="true" />
                    <span>{t('heat.info.split')}</span>
                  </div>
                </div>
              </UncontrolledTooltip>
            </th>
            <SortTh label={t('columns.mode')} title={t('columns.mode_tooltip')} sortKey="splitting_mode" col={sortCol} dir={sortDir} onToggle={onSort} />
            <th aria-label={t('columns.expand_aria')} />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={COLS} className="text-muted">{t('empty_table')}</td></tr>
          ) : rows.map((r, idx) => {
            const isOpen = openId === r.id;
            // SP-8 follow-up: evidenza "evasa parzialmente" — applicata solo
            // a fulfilled/confirmed (le altre fasi non sono in scope).
            // Rollback: niente row-highlight, solo il badge giallo accanto allo status.
            const isPartial = (r.partials_count ?? 0) > 0
              && (r.status === 'fulfilled' || r.status === 'confirmed');
            const rowCls = isOpen ? 'pl-row-open' : (idx % 2 === 1 ? 'pl-row-stripe' : undefined);
            return (
              <FragmentRow key={r.id}>
                <tr
                  onClick={() => onToggle(r.id)}
                  style={{ cursor: 'pointer' }}
                  className={rowCls}
                  aria-expanded={isOpen}
                >
                  <td className="font-monospace vp-code">{r.external_id}</td>
                  <td>{r.customer_name ?? <span className="text-muted">—</span>}</td>
                  <td>{formatBookingDate(r.booking_date)}</td>
                  <td>
                    {r.list_type
                      ? <StatusBadge tone={TIPO_TONES[r.list_type] ?? 'neutral'} className={TIPO_CLASS[r.list_type]}>
                          {t(`types_short.${r.list_type}`, r.list_type)}
                        </StatusBadge>
                      : '—'}
                  </td>
                  <td>
                    {(() => {
                      // Tipologia: set distinto degli item_type (V/S) della
                      // distinta → più badge affiancati per le liste miste.
                      // Fallback al campo head list_category (singolo) quando
                      // nessun articolo ha item_type valorizzato.
                      const cats = r.item_types
                        ? r.item_types.split(',').map((c) => c.trim()).filter(Boolean)
                        : (r.list_category ? [r.list_category] : []);
                      if (cats.length === 0) return '—';
                      return (
                        <span className="d-inline-flex flex-wrap gap-1">
                          {cats.map((c) => (
                            <StatusBadge key={c} tone={CATEGORIA_TONES[c] ?? 'neutral'} className={CATEGORIA_CLASS[c]}>
                              {t(`categories.${c}`, c)}
                            </StatusBadge>
                          ))}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    <PickListStatusBadge status={r.display_status ?? r.status} />
                    {r.auto_split_pending && r.status === 'open' && (
                      <span className="ms-1 d-inline-block"><PickListPendingSplitBadge /></span>
                    )}
                    {isPartial && (
                      <span className="ms-1 d-inline-block">
                        <PickListPartialBadge count={r.partials_count!} />
                      </span>
                    )}
                  </td>
                  <td>{r.distinct_ean_count ?? 0}</td>
                  <td className="small">{r.operator_emails ?? <span className="text-muted">—</span>}</td>
                  <td>{r.distinct_locations}</td>
                  <td>{r.distinct_locations_picked ?? 0}</td>
                  <td>{r.qty_total}</td>
                  <td>{r.qty_picked_total ?? 0}</td>
                  <td>
                    <PickListHeatBadge
                      input={{ locations: r.distinct_locations, totalQtyAssigned: r.qty_total }}
                      options={{ maxQtyPerMission: r.max_qty_per_mission }}
                    />
                  </td>
                  <td>{
                    // SP-8f-fix — la "Modalità assegnazione" è significativa solo
                    // da quando la lista è assegnata/splittata. Una lista `open`
                    // (Ricevuta, non assegnata a nessuno) ha splitting_mode='manual'
                    // di default nel DB → mostrare "—" invece di "Manuale".
                    r.status === 'open' || !r.splitting_mode
                      ? '—'
                      : t(`mode_values.${r.splitting_mode}`, { defaultValue: r.splitting_mode })
                  }</td>
                  <td className="text-end text-nowrap">
                    {onDownloadCsv && (
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 me-2 text-secondary"
                        title={t('actions.download_detail_csv')}
                        aria-label={t('actions.download_detail_csv')}
                        onClick={(e) => { e.stopPropagation(); onDownloadCsv(r.id); }}
                      >
                        <i className="bi bi-download" />
                      </Button>
                    )}
                    <i className={`bi ${isOpen ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
                  </td>
                </tr>
                <tr className="pl-accordion-row">
                  <td colSpan={COLS} className="p-0 border-0">
                    <Collapse isOpen={isOpen}>
                      <div className="p-3 bg-body-tertiary">
                        {isOpen && renderExpanded(r.id)}
                      </div>
                    </Collapse>
                  </td>
                </tr>
              </FragmentRow>
            );
          })}
        </tbody>
      </Table>
      </div>

      <div className="d-flex gap-2 align-items-center mt-2">
        <Button size="sm" color="light" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>{t('pagination.prev')}</Button>
        <span className="text-muted small">
          {t('pagination.page_of', { page, total: totalPages })} · {t('pagination.total_lists', { count: total })}
        </span>
        <Button size="sm" color="light" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>{t('pagination.next')}</Button>
      </div>
    </div>
  );
}

function FragmentRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

